import { useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export function KeyVisualPicker({ pageId, type, coverImageUrl, sourceLink, onUpdated }) {
  const fileInputRef = useRef(null);
  const [link, setLink] = useState(sourceLink || "");
  const [uploading, setUploading] = useState(false);
  const [fetching, setFetching] = useState(false);

  const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error("Image must be smaller than 20MB");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(`/pages/${pageId}/upload-image`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUpdated({ cover_image_url: data.cover_image_url, colors: data.colors });
      toast.success("Image uploaded — page colors updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleFetch = async () => {
    if (!link.trim()) return;
    setFetching(true);
    try {
      const { data } = await api.post(`/pages/${pageId}/fetch-artwork`, { source_link: link.trim() });
      onUpdated({ cover_image_url: data.cover_image_url, colors: data.colors, source_link: link.trim() });
      toast.success("Artwork fetched — page colors updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not fetch artwork from this link");
    } finally {
      setFetching(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="key-visual-picker">
      <div className="flex items-start gap-4">
        <div className={`shrink-0 rounded-xl overflow-hidden bg-white/5 border border-white/10 flex items-center justify-center ${type === "event" ? "w-36 aspect-[16/9]" : "w-28 h-28"}`}>
          {coverImageUrl ? (
            <img src={fileUrl(coverImageUrl)} alt="key visual" className="w-full h-full object-cover" data-testid="key-visual-preview" />
          ) : (
            <i className={type === "event" ? "fa-solid fa-calendar-days text-xl text-white/20" : "fa-solid fa-record-vinyl text-xl text-white/20"} />
          )}
        </div>
        <div className="flex-1 grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Upload manually</p>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" id="cover-upload-input" data-testid="cover-image-file-input" />
            <Button type="button" variant="outline" disabled={uploading} onClick={() => fileInputRef.current?.click()} className="w-full border-white/15 hover:bg-white/10" data-testid="upload-image-button">
              {uploading ? "Uploading..." : "Choose Image"}
            </Button>
          </div>
          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
              {type === "event" ? "Or paste a ticket link" : "Or paste Spotify / Apple Music link"}
            </p>
            <div className="flex gap-2">
              <Input
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder={type === "event" ? "https://tickets.example.com/..." : "https://open.spotify.com/track/..."}
                className="bg-white/5 border-white/15"
                data-testid="source-link-input"
              />
              <Button type="button" disabled={fetching} onClick={handleFetch} className="bg-emerald-500 hover:bg-emerald-600 shrink-0" data-testid="fetch-artwork-button">
                {fetching ? "..." : "Fetch"}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <p className="text-xs text-slate-500">The page's colors automatically adapt to match whichever image you set. Any dimension works — max file size 20MB.</p>
    </div>
  );
}
